import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Keyboard, Stack, TextInput } from 'grommet';
import { Box, BoxProps, Text } from '/src/components';
import { config } from '/src/config';
import { pxToRem } from '/src/utils';

export type AddressAutocompletePlaceResult = {
  line1: string;
  line2: string;
  city: string;
  stateShortCode: string;
  postCode: string;
};

export type AddressAutocompleteInputProps = BoxProps & {
  label: string;
  value: string;
  setValue: (value: string) => void;
  onPlaceSelected?: (place: AddressAutocompletePlaceResult) => void;
  name?: string;
  placeholder?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  onSubmit?: (event: React.KeyboardEvent<HTMLElement>) => void;
};

let googleMapsScriptLoaded = false;
let googleMapsScriptLoading = false;
const loadCallbacks: ((success: boolean) => void)[] = [];

interface WindowWithCallback extends Window {
  __googleMapsCallback?: () => void;
}

interface PlacePrediction {
  placeId: string;
  text: { text: string };
  toPlace: () => {
    fetchFields: (opts: { fields: string[] }) => Promise<void>;
    addressComponents: AddressComponent[] | null;
  };
}

interface AutocompleteSuggestion {
  placePrediction: PlacePrediction;
}

interface AddressComponent {
  types: string[];
  longText?: string;
  long_name?: string;
  shortText?: string;
  short_name?: string;
}

function loadGoogleMapsScript(): Promise<boolean> {
  return new Promise((resolve) => {
    if (googleMapsScriptLoaded) {
      resolve(true);
      return;
    }

    loadCallbacks.push(resolve);

    if (googleMapsScriptLoading) return;
    googleMapsScriptLoading = true;

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${config.maps.googleMapsApiKey}&libraries=places&loading=async&callback=__googleMapsCallback`;
    script.async = true;
    script.defer = true;

    (window as WindowWithCallback).__googleMapsCallback = () => {
      googleMapsScriptLoaded = true;
      loadCallbacks.forEach((cb) => cb(true));
      loadCallbacks.length = 0;
      delete (window as WindowWithCallback).__googleMapsCallback;
    };

    script.onerror = () => {
      googleMapsScriptLoading = false;
      delete (window as WindowWithCallback).__googleMapsCallback;
      script.remove();
      const pending = loadCallbacks.splice(0);
      pending.forEach((cb) => cb(false));
    };

    document.head.appendChild(script);
  });
}

type Prediction = {
  placePrediction: PlacePrediction;
  description: string;
};

async function fetchPredictions(input: string): Promise<Prediction[]> {
  try {
    const AutocompleteSuggestion = (
      google.maps.places as unknown as {
        AutocompleteSuggestion: {
          fetchAutocompleteSuggestions: (opts: {
            input: string;
            includedPrimaryTypes: string[];
            includedRegionCodes: string[];
          }) => Promise<{ suggestions: AutocompleteSuggestion[] }>;
        };
      }
    ).AutocompleteSuggestion;
    const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input,
      includedPrimaryTypes: ['street_address', 'subpremise', 'premise'],
      includedRegionCodes: ['us', 'ca'],
    });
    return (suggestions || []).map((s) => ({
      placePrediction: s.placePrediction,
      description: s.placePrediction.text.text,
    }));
  } catch {
    return [];
  }
}

function getComponentText(component: AddressComponent, preferShort = false): string {
  if (preferShort) {
    return component.shortText ?? component.short_name ?? component.longText ?? component.long_name ?? '';
  }
  return component.longText ?? component.long_name ?? component.shortText ?? component.short_name ?? '';
}

function parseAddressComponents(components: AddressComponent[]): AddressAutocompletePlaceResult {
  let streetNumber = '';
  let route = '';
  let city = '';
  let stateShortCode = '';
  let postCode = '';
  let subpremise = '';

  for (const component of components) {
    const types = component.types;
    if (types.includes('street_number')) {
      streetNumber = getComponentText(component);
    } else if (types.includes('route')) {
      route = getComponentText(component);
    } else if (types.includes('subpremise')) {
      subpremise = getComponentText(component);
    } else if (types.includes('locality')) {
      city = getComponentText(component);
    } else if (types.includes('sublocality_level_1') && !city) {
      city = getComponentText(component);
    } else if (types.includes('administrative_area_level_1')) {
      stateShortCode = getComponentText(component, true);
    } else if (types.includes('postal_code')) {
      postCode = getComponentText(component);
    }
  }

  return {
    line1: streetNumber ? `${streetNumber} ${route}` : route,
    line2: subpremise ? `#${subpremise}` : '',
    city,
    stateShortCode,
    postCode,
  };
}

async function getPlaceDetails(placePrediction: PlacePrediction): Promise<AddressAutocompletePlaceResult | null> {
  try {
    const place = placePrediction.toPlace();
    await place.fetchFields({ fields: ['addressComponents'] });
    if (!place.addressComponents) return null;
    return parseAddressComponents(place.addressComponents);
  } catch {
    return null;
  }
}

export const AddressAutocompleteInput: React.FC<AddressAutocompleteInputProps> = (props) => {
  const {
    label,
    value,
    setValue,
    onPlaceSelected,
    name,
    placeholder,
    error,
    required,
    disabled,
    onSubmit,
    ...boxProps
  } = props;

  const [isFocused, setIsFocused] = useState(false);
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [apiReady, setApiReady] = useState(googleMapsScriptLoaded);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);
  const skipFetchRef = useRef(false);

  const isActiveLabel = value !== '' || isFocused || disabled || !!placeholder;

  useEffect(() => {
    if (disabled || !config.maps.googleMapsApiKey) return;
    loadGoogleMapsScript().then((success) => setApiReady(success));
  }, [disabled]);

  useEffect(() => {
    if (!apiReady || skipFetchRef.current || !isFocused) {
      skipFetchRef.current = false;
      return;
    }

    if (!value || value.length < 3) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const results = await fetchPredictions(value);
      setPredictions(results);
      setShowDropdown(results.length > 0);
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value, apiReady, isFocused]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = useCallback(
    async (prediction: Prediction) => {
      setShowDropdown(false);
      setPredictions([]);

      const result = await getPlaceDetails(prediction.placePrediction);
      if (result) {
        skipFetchRef.current = true;
        setValue(result.line1);
        onPlaceSelected?.(result);
      }
    },
    [setValue, onPlaceSelected]
  );

  const borderColor = error ? 'red' : isFocused ? 'accent-1' : 'light-5';

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <Box {...boxProps}>
        <Box
          border={disabled ? undefined : { color: borderColor, size: 'small' }}
          background={disabled ? 'light-6' : undefined}
          round="6px"
          height={pxToRem(58)}
        >
          <Box round="6px" direction="row" fill="vertical">
            <Stack anchor={isActiveLabel ? 'top-left' : 'left'} guidingChild="last" fill="vertical">
              <Box pad={{ top: isActiveLabel ? 'xsmall' : undefined, left: 'small' }}>
                <Text size={isActiveLabel ? 'small' : 'medium'} color={isActiveLabel ? 'accent-1' : undefined}>
                  {label}
                  {required && '*'}
                </Text>
              </Box>
              <Keyboard onEnter={onSubmit}>
                <TextInput
                  id={name}
                  name={name}
                  value={value}
                  placeholder={placeholder}
                  onChange={(e) => setValue(e.target.value)}
                  required={required}
                  disabled={disabled}
                  onFocus={() => {
                    setIsFocused(true);
                    if (predictions.length > 0) setShowDropdown(true);
                  }}
                  onBlur={() => setIsFocused(false)}
                  autoComplete="off"
                  plain
                  style={{
                    fontFamily: 'Lato, sans-serif',
                    height: pxToRem(58),
                    paddingTop: pxToRem(26),
                    paddingBottom: pxToRem(10),
                  }}
                  className="hide-input-arrows"
                />
              </Keyboard>
            </Stack>
          </Box>
        </Box>
        {showDropdown && predictions.length > 0 && (
          <Box
            background="white"
            elevation="medium"
            round="6px"
            style={{
              position: 'absolute',
              top: pxToRem(62),
              left: 0,
              right: 0,
              zIndex: 10000,
              maxHeight: '240px',
              overflowY: 'auto',
            }}
          >
            {predictions.map((prediction) => (
              <Box
                key={prediction.placePrediction.placeId}
                pad={{ horizontal: 'small', vertical: 'xsmall' }}
                hoverIndicator="light-2"
                onClick={() => handleSelect(prediction)}
                style={{ cursor: 'pointer' }}
              >
                <Text size="small" style={{ fontFamily: 'Lato, sans-serif' }}>
                  {prediction.description}
                </Text>
              </Box>
            ))}
          </Box>
        )}
        {error && (
          <Box margin={{ top: '0.5rem', left: '0.75rem' }}>
            <Text color="red" size="0.75rem">
              {error}
            </Text>
          </Box>
        )}
      </Box>
    </div>
  );
};
